/**
 * Local storage.
 *
 * IndexedDB, through Dexie, is the source of truth. The Worker is a replica.
 * Everything the app does -- adding, editing, importing, reviewing -- works
 * with the network switched off and with cloud sync never turned on.
 */
import Dexie, { type Table } from 'dexie';
import {
  DEFAULT_CATEGORIES,
  type Account,
  type Category,
  type MerchantRule,
  type Settings,
  type Transaction,
} from '@wis/shared';

/** One entry per local change that still has to reach the Worker. */
export interface SyncQueueItem {
  /** Auto-incremented, so the queue drains in the order changes were made. */
  id?: number;
  transactionId: string;
  operation: 'upsert';
  queuedAt: string;
  attempts: number;
  lastAttemptAt?: string;
  lastError?: string;
}

export interface MetaRecord {
  key: string;
  value: unknown;
  updatedAt: string;
}

export const SETTINGS_KEY = 'settings';

export class WhatISpentDatabase extends Dexie {
  transactions!: Table<Transaction, string>;
  categories!: Table<Category, string>;
  accounts!: Table<Account, string>;
  merchantRules!: Table<MerchantRule, string>;
  syncQueue!: Table<SyncQueueItem, number>;
  meta!: Table<MetaRecord, string>;

  constructor(name = 'what-i-spent') {
    super(name);

    // Indexes chosen for the three screens that actually query: Today (by
    // occurredAt), Transactions (by month and category) and Review (by status).
    this.version(1).stores({
      transactions:
        'id, occurredAt, updatedAt, categoryId, source, reviewStatus, dedupeKey, accountLabel, deletedAt, [reviewStatus+deletedAt]',
      categories: 'id, sortOrder, archived',
      accounts: 'id, label, archived',
      merchantRules: 'id, pattern, categoryId, priority',
      syncQueue: '++id, transactionId, queuedAt',
      meta: 'key',
    });
  }
}

export const db = new WhatISpentDatabase();

export const DEFAULT_SETTINGS: Settings = {
  monthlyBudgetSen: 0,
  currency: 'MYR',
  timeZone: 'Asia/Kuala_Lumpur',
  defaultAccountLabel: 'Cash',
  defaultCategoryId: 'other',
  syncEnabled: false,
  apiBaseUrl: '',
  storeAttachmentsInCloud: false,
  theme: 'system',
  updatedAt: '2024-01-01T00:00:00.000Z',
};

const STARTER_ACCOUNTS: Omit<Account, 'id'>[] = [
  { label: 'Cash', kind: 'cash', isDefault: true, archived: false, updatedAt: '2024-01-01T00:00:00.000Z' },
  { label: 'MAE', kind: 'ewallet', isDefault: false, archived: false, updatedAt: '2024-01-01T00:00:00.000Z' },
  {
    label: 'TNG eWallet',
    kind: 'ewallet',
    isDefault: false,
    archived: false,
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
  {
    // A label, never a card number. The app has nowhere to put one.
    label: 'Apple Pay',
    kind: 'card',
    isDefault: false,
    archived: false,
    updatedAt: '2024-01-01T00:00:00.000Z',
  },
];

/**
 * Fill in the default categories and account labels on first run.
 * Safe to call on every start: it only writes what is missing.
 */
export async function ensureSeeded(database: WhatISpentDatabase = db): Promise<void> {
  await database.transaction('rw', database.categories, database.accounts, database.meta, async () => {
    if ((await database.categories.count()) === 0) {
      await database.categories.bulkPut(DEFAULT_CATEGORIES);
    }
    if ((await database.accounts.count()) === 0) {
      await database.accounts.bulkPut(
        STARTER_ACCOUNTS.map((account) => ({ ...account, id: crypto.randomUUID() })),
      );
    }
    if (!(await database.meta.get(SETTINGS_KEY))) {
      await database.meta.put({
        key: SETTINGS_KEY,
        value: DEFAULT_SETTINGS,
        updatedAt: new Date().toISOString(),
      });
    }
  });
}

export async function readSettings(database: WhatISpentDatabase = db): Promise<Settings> {
  const record = await database.meta.get(SETTINGS_KEY);
  return { ...DEFAULT_SETTINGS, ...((record?.value as Partial<Settings>) ?? {}) };
}

export async function writeSettings(
  patch: Partial<Settings>,
  database: WhatISpentDatabase = db,
): Promise<Settings> {
  const current = await readSettings(database);
  const next: Settings = { ...current, ...patch, updatedAt: new Date().toISOString() };
  await database.meta.put({ key: SETTINGS_KEY, value: next, updatedAt: next.updatedAt });
  return next;
}

export async function readMeta<T>(key: string, database: WhatISpentDatabase = db): Promise<T | undefined> {
  return (await database.meta.get(key))?.value as T | undefined;
}

export async function writeMeta(
  key: string,
  value: unknown,
  database: WhatISpentDatabase = db,
): Promise<void> {
  await database.meta.put({ key, value, updatedAt: new Date().toISOString() });
}
